import { P2Role, type RecordEntityId, type Site, type SiteLocation } from '@centric-os/types';
import type { AxiosResponse } from 'axios';
import { defineStore, type StateTree } from 'pinia';
import { useFileConverter } from '@centric-os/helpers';
import { cloneDeep, get } from 'lodash-es';
import { useAuthStore } from '@centric-os/stores/auth/use-auth-store';
import type { ValidateVertexResponse } from '@compassdigital/sdk.typescript/interface/centricos';

/**
 * @interface State
 * @extends {StateTree}
 * @description Defines the state structure for the business unit store.
 */
interface State extends StateTree {
  /**
   * @property {SiteLocation | null} businessUnit - The current business unit data.
   */
  businessUnit: SiteLocation | null;
}

/**
 * @function useBusinessUnitStore
 * @param {string} [storeId='siteLocation'] - The ID for the Pinia store.
 * @returns {ReturnType<typeof defineStore>} A configured Pinia store instance.
 * @description Creates and returns a Pinia store for managing business unit data.
 */
export function useBusinessUnitStore(storeId = 'siteLocation') {
  const store = defineStore(storeId, {
    state: (): State => ({
      businessUnit: null,
    }),
    actions: {
      /**
       * @async
       * @function createBusinessUnit
       * @param {Partial<SiteLocation>} payload - Partial data for creating a new business unit.
       * @param {RecordEntityId} siteId - The ID of the site to associate with the new business unit.
       * @returns {Promise<void>}
       * @description Creates a new business unit and associates it with the specified site.
       * @throws {Error} If the API requests fail.
       */
      async createBusinessUnit(
        payload: Partial<SiteLocation>,
        siteId: RecordEntityId,
      ): Promise<void> {
        await this.cdlApi
          .post<SiteLocation, AxiosResponse<SiteLocation, any>, Partial<SiteLocation>>(
            '/location',
            {
              ...payload,
            },
          )
          .then((response) =>
            this.cdlApi.patch<Site>(`/location/group/${siteId}`, {
              locations: [{ id: response.data.id }],
            }),
          );
      },

      /**
       * @async
       * @function getBusinessUnit
       * @param {RecordEntityId} id - The ID of the business unit to retrieve.
       * @returns {Promise<void>}
       * @description Fetches a business unit by its ID and updates the store state.
       * @throws {Error} If the API request fails.
       */
      async getBusinessUnit(id: RecordEntityId): Promise<void> {
        const response = await this.cdlApi.get<SiteLocation>(`/location/${id}`, {
          params: {
            nocache: true,
            show_disabled_marketplace: true,
          },
        });
        this.businessUnit = response.data;
      },

      /**
       * @async
       * @function updateBusinessUnit
       * @param {Partial<SiteLocation>} formData - Partial data for updating the business unit.
       * @param {RecordEntityId} id - The ID of the business unit to update.
       * @returns {Promise<void>}
       * @description Updates a business unit with the given payload and updates the store state.
       * @throws {Error} If the API request fails.
       */
      async updateBusinessUnit(formData: Partial<SiteLocation>, id: RecordEntityId): Promise<void> {
        const payload: Partial<SiteLocation> = cloneDeep(formData);
        if (payload?.market_place?.logo && payload.market_place.logo instanceof File) {
          const url = await this.uploadFile(payload.market_place.logo);
          payload.market_place.logo = url;
        }
        payload.market_place.is.pickup_supported = payload.market_place.is.enabled;
        const response = await this.cdlApi.patch<SiteLocation>(`/location/${id}`, payload);
        this.businessUnit = response.data;
      },

      /**
       * @async
       * @function uploadFile
       * @param {File} file - The file to be uploaded.
       * @returns {Promise<string>} The URL of the uploaded file.
       * @description Uploads a file and returns its URL.
       * @throws {Error} If the file upload fails.
       */
      async uploadFile(file: File): Promise<string> {
        try {
          const { getBase64 } = useFileConverter();
          const logoBase64: string = (await getBase64(file)) as string;
          const { data } = await this.cdlApi.post('/file', {
            file_base64: logoBase64.replace('data:image/png;base64,', ''),
            file_name: file.name.replace(/\s/g, '_'),
          });
          return data.url;
        } catch (error) {
          console.error('Error uploading logo', error);
          throw error;
        }
      },
      async enableVertex(id: RecordEntityId): Promise<ValidateVertexResponse> {
        const response = await this.cdlApi.post<ValidateVertexResponse>(
          `/centricos/business-unit/${id}/validate-vertex`,
        );
        return response.data;
      },

      async deleteBusinessUnit(businessUnitId: RecordEntityId): Promise<void> {
        await this.cdlApi.delete(`/centricos/business-unit/${businessUnitId}`);
      },
    },
    getters: {
      currentBusinessUnit: (state): Partial<SiteLocation> => state.businessUnit,
      isMarketPlaceEnabled: (state: State): boolean => {
        return !!get(
          state.businessUnit,
          'market_place.is.enabled',
          get(state.businessUnit, 'market_place.label.en', false),
        );
      },
      canModifyMarketPlace: (_state: State): boolean => {
        const authStore = useAuthStore();
        return authStore.hasRole(P2Role.ADMIN) || authStore.hasRole(P2Role.SITE_OPERATOR);
      },
    },
  });
  return store();
}
